/**
 * Calendar Sync Integration (Phase 4 — Steps 4.4, 4.5).
 *
 * Bidirectional calendar sync with Google Calendar, Apple Calendar, Outlook.
 * Stub implementation — requires external API credentials for production use.
 */

/** Supported calendar providers */
export type CalendarProvider = 'google' | 'apple' | 'outlook';

/** Calendar sync settings */
export interface CalendarSyncConfig {
  /** Which provider */
  provider: CalendarProvider;
  /** OAuth access token (stored in memory only) */
  accessToken: string | null;
  /** Calendar ID to sync with */
  calendarId: string | null;
  /** Whether auto-sync is enabled */
  autoSync: boolean;
  /** What to sync */
  syncMode: 'all' | 'absences_only' | 'sessions_only';
}

/** A calendar event representation */
export interface CalendarEvent {
  /** Event title */
  title: string;
  /** ISO datetime start */
  start: string;
  /** ISO datetime end */
  end: string;
  /** Event description */
  description: string;
  /** Color code (provider-specific) */
  colorId?: string;
  /** Provider event ID (null if not yet synced) */
  externalId: string | null;
}

/** Result of a sync operation */
export interface SyncResult {
  /** Events successfully synced */
  synced: number;
  /** Events that failed */
  failed: number;
  /** Error messages */
  errors: string[];
}

/** Default sync config */
export const DEFAULT_SYNC_CONFIG: CalendarSyncConfig = {
  provider: 'google',
  accessToken: null,
  calendarId: null,
  autoSync: false,
  syncMode: 'absences_only',
};

/**
 * Generate an OAuth authorization URL for the given provider.
 * In production, this would redirect to the provider's consent screen.
 */
export function getAuthUrl(provider: CalendarProvider): string {
  const clientId = import.meta.env.VITE_CALENDAR_CLIENT_ID ?? 'stub-client-id';
  const redirectUri = typeof window !== 'undefined' ? window.location.origin : 'http://localhost:5173';

  switch (provider) {
    case 'google':
      return `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${redirectUri}&scope=https://www.googleapis.com/auth/calendar.events&response_type=code`;
    case 'outlook':
      return `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=${clientId}&redirect_uri=${redirectUri}&scope=Calendars.ReadWrite&response_type=code`;
    case 'apple':
      // Apple Calendar uses CalDAV — no simple OAuth URL
      return '';
  }
}

/**
 * Create a calendar event for an absence.
 * Stub — in production, this would call the provider's API.
 */
export async function createAbsenceEvent(
  _config: CalendarSyncConfig,
  _event: CalendarEvent,
): Promise<string | null> {
  // Stub: would make API call to create event
  // Returns the external event ID on success
  if (!_config.accessToken) {
    throw new Error('Not authenticated. Please connect your calendar first.');
  }
  // Simulate API call
  return `stub-event-${Date.now()}`;
}

/**
 * Update an existing calendar event.
 */
export async function updateAbsenceEvent(
  _config: CalendarSyncConfig,
  _externalId: string,
  _event: CalendarEvent,
): Promise<boolean> {
  if (!_config.accessToken) {
    throw new Error('Not authenticated. Please connect your calendar first.');
  }
  return true;
}

/**
 * Delete a calendar event.
 */
export async function deleteAbsenceEvent(
  _config: CalendarSyncConfig,
  _externalId: string,
): Promise<boolean> {
  if (!_config.accessToken) {
    throw new Error('Not authenticated. Please connect your calendar first.');
  }
  return true;
}

/**
 * Sync all sessions to the calendar.
 */
export async function syncSessionsToCalendar(
  _config: CalendarSyncConfig,
  _events: CalendarEvent[],
): Promise<SyncResult> {
  if (!_config.accessToken) {
    return { synced: 0, failed: _events.length, errors: ['Not authenticated'] };
  }
  // Stub: would batch create/update events
  return { synced: _events.length, failed: 0, errors: [] };
}

/**
 * Generate an .ics file content for download.
 * This works offline without any API connection.
 */
export function generateICSFile(events: CalendarEvent[]): string {
  const lines: string[] = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Absence Budget//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
  ];

  for (const event of events) {
    lines.push('BEGIN:VEVENT');
    lines.push(`DTSTART:${formatICSDate(event.start)}`);
    lines.push(`DTEND:${formatICSDate(event.end)}`);
    lines.push(`SUMMARY:${escapeICS(event.title)}`);
    lines.push(`DESCRIPTION:${escapeICS(event.description)}`);
    lines.push(`UID:abs-${Date.now()}-${Math.random().toString(36).slice(2)}`);
    lines.push('END:VEVENT');
  }

  lines.push('END:VCALENDAR');
  return lines.join('\r\n');
}

function formatICSDate(isoDate: string): string {
  // Convert ISO datetime to ICS format: YYYYMMDDTHHMMSSZ
  return isoDate.replace(/[-:]/g, '').replace(/\.\d{3}/, '').replace(/Z$/, 'Z');
}

function escapeICS(text: string): string {
  return text.replace(/[\\;,\n]/g, (ch) => {
    if (ch === '\n') return '\\n';
    return '\\' + ch;
  });
}
